import { fetchWithAuth } from './api';
import { createPublicAvatarUrl } from '../config/avatar';

// Define the interface for an avatar option
export interface AvatarOption {
  id: string | number;
  name: string;
  url: string;
  publicUrl?: string;
}

// Function to fetch avatars from the API
export const fetchAvatarOptions = async (): Promise<AvatarOption[]> => {
  try {
    // Instead of calling the backend API that might fail due to Supabase config issues,
    // we'll use the hard-coded avatar URLs
    const defaultAvatars = [
      { id: 1, name: "avatar1.svg" },
      { id: 2, name: "avatar2.svg" },
      { id: 3, name: "avatar3.svg" },
      { id: 4, name: "avatar4.svg" },
      { id: 5, name: "avatar5.svg" },
      { id: 6, name: "avatar6.svg" },
      { id: 7, name: "avatar7.svg" },
      { id: 8, name: "avatar8.svg" }
    ];
    
    // Generate public URLs for each avatar
    const avatarsWithUrls = defaultAvatars.map(avatar => ({
      ...avatar,
      url: `profiles/${avatar.name}`,
      publicUrl: createPublicAvatarUrl(avatar.name)
    }));
    
    return avatarsWithUrls;
  } catch (err) {
    console.error('Error creating avatar URLs:', err);
    return [];
  }
};

/**
 * Gets an avatar URL for a username
 * @param username The username to get an avatar for
 * @returns The avatar URL
 */
export const getAvatarForUser = async (username: string): Promise<string | null> => {
  try {
    // Try to fetch the user's profile to get their avatar URL
    const userData = await fetchWithAuth(`/users/${username}/`);
    
    if (userData && userData.avatar_url) {
      // If the avatar URL points to Supabase storage (contains profiles/), generate a public URL
      if (userData.avatar_url.includes('profiles/')) {
        const filename = userData.avatar_url.split('profiles/')[1];
        const publicUrl = createPublicAvatarUrl(filename);
        return publicUrl;
      }
      
      // If not a Supabase URL, use the raw URL
      return userData.avatar_url;
    }
  } catch (err) {
    console.error(`Error fetching avatar for user ${username}:`, err);
  }
  
  // Return a default avatar if no avatar is found
  return createPublicAvatarUrl("avatar1.svg");
};

/**
 * Generates a hash code for a string
 * @param str The string to hash
 * @returns A number hash
 */
export const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

/**
 * Helper function to get user data with avatar
 */
export const getUserData = async (username: string, silent = false): Promise<any> => {
  // Import fetchWithAuth
  const { fetchWithAuth } = await import('./api');
  
  try {
    // Try to get user from the API
    const endpoints = [
      `/api/users/${username}/`,
      `/api/users/me/`,
      `/users/${username}/`, 
      `/users/profile/`
    ];
    
    let userData = null;
    
    for (const endpoint of endpoints) {
      try {
        userData = await fetchWithAuth(endpoint, {}, undefined, true);
        if (userData) break;
      } catch (err) {
        // Continue to next endpoint
      }
    }
    
    // If we got user data but it doesn't have an avatar, add one
    if (userData && !userData.avatar_url) {
      userData.avatar_url = getAvatarForUser(userData.username || username);
    }
    
    // If we didn't get user data, create a minimal user object
    if (!userData) {
      userData = {
        username: username,
        email: localStorage.getItem("email") || "",
        first_name: localStorage.getItem("first_name") || "",
        last_name: localStorage.getItem("last_name") || ""
      };
    }
    
    // Ensure avatar_url is always set
    userData.avatar_url = userData.avatar_url || getAvatarForUser(username);
    
    return userData;
  } catch (err) {
    // Return a minimal user object with avatar
    return {
      username: username,
      avatar_url: getAvatarForUser(username)
    };
  }
};